'use client'

import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import TuneIcon from '@mui/icons-material/Tune'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Skeleton,
    Typography,
    alpha
} from '@mui/material'
import React, { useState } from 'react'

import DomainBalanceCarouselWrapper from '@/src/components/common/wallet/domainBalanceCarousel'
import { CleartripTransactions } from '@/src/components/common/wallet/cleartripTransactions'
import type { CleartripFormattedTransaction } from '@/src/components/common/wallet/cleartripTransactions/cleartripTransactions'
import WalletEmptyStateWrapper from '@/src/components/common/wallet/walletEmptyState'
import { WALLET_TABS } from '@/src/constants/wallet'
import { AllWalletTabType, BrandBalance, DomainBalance, FetchControl } from '@/src/types/wallet'
import { formatDisplayDate, getDaysUntilExpiry } from '@/src/utils/wallet'

interface FormattedTransaction {
    id: string
    source: string
    reference: string | null
    amount: number
    currency: string
    expiryDate: string
    transactionDate: string
    rawSourceType: string
    iconType: 'refresh' | 'gift' | 'card'
    txnType?: string
}

interface WalletDesktopPageProps {
    // Brand — used only for conditionally hiding info icon and Flyin transactions section
    isCleartrip: boolean

    // Balance data
    displayBalance: number
    displayCurrency: string
    domainBalances: DomainBalance[]
    hasMultipleDomainBalances: boolean

    // Transactions data
    formattedTransactions: FormattedTransaction[]
    cleartripFallbackTransactions?: CleartripFormattedTransaction[]
    cleartripTransactionLabels?: {
        transactionHistoryLabel: string
        noTransactionsLabel: string
        loadMoreLabel: string
        loadingLabel: string
        transactionIdLabel: string
    }
    hasMoreTransactions: boolean
    brandBalances: BrandBalance[]

    // Tab state
    activeTab: AllWalletTabType
    onTabChange: (tab: AllWalletTabType) => void
    brandFilter: FetchControl
    onBrandFilterChange: (filter: FetchControl) => void

    // Loading states
    isLoadingBalance: boolean
    isLoadingTransactions: boolean
    isFetchingMore: boolean

    // Error states
    balanceError: string | null
    transactionsError: string | null

    // Actions
    onLoadMore: () => void
    onRefresh: () => void

    onOpenFAQ?: () => void

    // Formatting
    formatAmount: (amount: number, currency: string) => string

    // Translations
    t: (key: string, values?: Record<string, any>) => string
    tCommon: (key: string) => string
    aboutWalletLabel: string

    // Locale
    locale: string
    isRtl: boolean

    // AB-driven multi-brand toggle
    showMultiBrandWallet: boolean
}

const WalletDesktopPage: React.FC<WalletDesktopPageProps> = ({
    isCleartrip,
    displayBalance,
    displayCurrency,
    domainBalances,
    hasMultipleDomainBalances,
    formattedTransactions,
    cleartripFallbackTransactions = [],
    cleartripTransactionLabels,
    hasMoreTransactions,
    brandBalances,
    activeTab,
    onTabChange,
    brandFilter,
    onBrandFilterChange,
    isLoadingBalance,
    isLoadingTransactions,
    isFetchingMore,
    balanceError,
    transactionsError,
    onLoadMore,
    onRefresh,
    onOpenFAQ,
    formatAmount,
    t,
    tCommon,
    aboutWalletLabel,
    locale,
    isRtl,
    showMultiBrandWallet
}) => {
    const shouldShowBalanceCarousel = brandBalances.length > 0 || domainBalances.length > 0
    const componentRadius = '16px'
    const isCleartripFilterOnly = showMultiBrandWallet && brandFilter === 'LINKED'
    const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null)

    const getIconComponent = (iconType: 'refresh' | 'gift' | 'card') => {
        switch (iconType) {
            case 'refresh':
                return RefreshIcon
            case 'gift':
                return CardGiftcardOutlinedIcon
            case 'card':
            default:
                return CreditCardOutlinedIcon
        }
    }

    const handleOpenFilterMenu = (event: React.MouseEvent<HTMLElement>) => {
        setFilterMenuAnchor(event.currentTarget)
    }

    const handleCloseFilterMenu = () => {
        setFilterMenuAnchor(null)
    }

    const handleSelectFilter = (filter: FetchControl) => {
        onBrandFilterChange(filter)
        handleCloseFilterMenu()
    }

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', bgcolor: 'transparent' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {t('title')}
                    </Typography>
                </Box>
            </Box>

            {shouldShowBalanceCarousel ? (
                <Box sx={{ mt: 2, mb: isCleartrip ? 0 : 2 }}>
                    <DomainBalanceCarouselWrapper
                        displayBalance={displayBalance}
                        displayCurrency={displayCurrency}
                        domainBalances={domainBalances}
                        brandBalances={brandBalances}
                        formatAmount={formatAmount}
                        t={t}
                        isDesktop
                        attachBottom={!!onOpenFAQ}
                    />
                    {onOpenFAQ && (
                        <Card
                            elevation={0}
                            sx={{
                                mt: 0,
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0,
                                borderBottomLeftRadius: componentRadius,
                                borderBottomRightRadius: componentRadius,
                                border: 1,
                                borderTop: 0,
                                borderColor: 'divider'
                            }}
                        >
                            <Box
                                onClick={onOpenFAQ}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2.5,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        color: 'text.secondary',
                                        mr: 1
                                    }}
                                >
                                    <HelpOutlineIcon sx={{ fontSize: 16 }} />
                                </Box>
                                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, color: 'text.primary' }}>
                                    {aboutWalletLabel}
                                </Typography>
                                <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </Box>
                        </Card>
                    )}
                </Box>
            ) : (
                <Card
                    elevation={0}
                    sx={{
                        background: 'white',
                        color: 'text.primary',
                        borderRadius: componentRadius,
                        mt: 3,
                        mb: isCleartrip ? 0 : 4,
                        border: 1,
                        borderColor: 'divider'
                    }}
                >
                    <CardContent sx={{ p: 4, pb: onOpenFAQ ? 2 : 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <AccountBalanceWalletIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                {t('walletBalance')}
                            </Typography>
                        </Box>

                        {isLoadingBalance ? (
                            <Skeleton variant="text" width={200} height={60} sx={{ bgcolor: 'grey.200' }} />
                        ) : balanceError ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {balanceError}
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontSize: '1.8rem',
                                        fontWeight: 700,
                                        letterSpacing: '-0.5px',
                                        mb: 1,
                                        color: 'text.primary'
                                    }}
                                >
                                    {formatAmount(displayBalance, displayCurrency)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block' }}
                                >
                                    {t('usableOnlyInCurrency', { currency: displayCurrency })}
                                </Typography>
                            </>
                        )}
                    </CardContent>

                    {onOpenFAQ && (
                        <Box
                            onClick={onOpenFAQ}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: 4,
                                py: 2,
                                borderTop: 1,
                                borderColor: 'divider',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    bgcolor: 'grey.200',
                                    color: 'text.secondary',
                                    mr: 1.5
                                }}
                            >
                                <HelpOutlineIcon sx={{ fontSize: 20 }} />
                            </Box>
                            <Typography variant="body1" sx={{ flex: 1, fontWeight: 600, color: 'text.primary' }}>
                                {aboutWalletLabel}
                            </Typography>
                            <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
                        </Box>
                    )}
                </Card>
            )}

            {/* Flyin Wallet Activity, Tabs, and Transactions — hidden for Cleartrip (uses separate component) */}
            {!isCleartrip && (
                <>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: componentRadius,
                            border: 1,
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            p: 3
                        }}
                    >
                        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.25rem' }}>
                                {t('walletActivity')}
                            </Typography>
                            {showMultiBrandWallet && (
                                <>
                                    <IconButton
                                        size="small"
                                        onClick={handleOpenFilterMenu}
                                        sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
                                    >
                                        <TuneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    </IconButton>
                                    <Menu
                                        anchorEl={filterMenuAnchor}
                                        open={Boolean(filterMenuAnchor)}
                                        onClose={handleCloseFilterMenu}
                                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                        slotProps={{
                                            paper: {
                                                sx: {
                                                    mt: 1,
                                                    p: 1.5,
                                                    borderRadius: 3,
                                                    minWidth: 220
                                                }
                                            }
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ px: 1, pb: 1, fontWeight: 600 }}>
                                            {t('paidVia')}
                                        </Typography>
                                        <MenuItem
                                            onClick={() => handleSelectFilter('MAIN')}
                                            sx={{
                                                borderRadius: 2,
                                                fontSize: '0.9rem',
                                                color: 'text.secondary',
                                                bgcolor: brandFilter === 'MAIN' ? 'grey.100' : 'transparent'
                                            }}
                                        >
                                            {t('brandFilters.flyinWallet')}
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => handleSelectFilter('LINKED')}
                                            sx={{
                                                mt: 0.5,
                                                borderRadius: 2,
                                                fontSize: '0.9rem',
                                                color: 'text.secondary',
                                                bgcolor: brandFilter === 'LINKED' ? 'grey.100' : 'transparent'
                                            }}
                                        >
                                            {t('brandFilters.cleartripWallet')}
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}
                        </Box>

                        {!isCleartripFilterOnly && (
                            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                                {WALLET_TABS.map(tab => (
                                    <Button
                                        key={tab}
                                        variant={activeTab === tab ? 'contained' : 'text'}
                                        size="small"
                                        onClick={() => onTabChange(tab)}
                                        sx={{
                                            borderRadius: 2,
                                            px: 2,
                                            py: 0.75,
                                            fontWeight: activeTab === tab ? 600 : 500,
                                            textTransform: 'capitalize',
                                            bgcolor: activeTab === tab ? 'primary.main' : 'transparent',
                                            color: activeTab === tab ? 'white' : 'text.secondary',
                                            '&:hover': {
                                                bgcolor:
                                                    activeTab === tab
                                                        ? 'primary.dark'
                                                        : theme => alpha(theme.palette.primary.main, 0.08)
                                            }
                                        }}
                                    >
                                        {t(`tabs.${tab.toLowerCase()}`)}
                                    </Button>
                                ))}
                            </Box>
                        )}

                        {/* Transactions List */}
                        <Box>
                        {isLoadingTransactions && !isFetchingMore ? (
                            Array.from({ length: 3 }).map((_, i) => (
                                <Card
                                    key={i}
                                    elevation={0}
                                    sx={{
                                        mb: 1.5,
                                        bgcolor: 'background.paper',
                                        borderRadius: componentRadius,
                                        border: 1,
                                        borderColor: 'divider'
                                    }}
                                >
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 1.75 } }}>
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                            <Skeleton
                                                variant="circular"
                                                width={48}
                                                height={48}
                                                sx={{ bgcolor: 'grey.200', flexShrink: 0 }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                                <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.75 }} />
                                                <Skeleton variant="text" width="50%" height={16} />
                                            </Box>
                                            <Box sx={{ textAlign: 'right', flexShrink: 0, pt: 0.5 }}>
                                                <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
                                                <Skeleton
                                                    variant="rectangular"
                                                    width={100}
                                                    height={22}
                                                    sx={{ borderRadius: '12px', ml: 'auto' }}
                                                />
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))
                        ) : transactionsError ? (
                            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                                <Typography variant="body2">{transactionsError}</Typography>
                                <Button variant="text" size="small" onClick={onRefresh} sx={{ mt: 1 }}>
                                    {tCommon('retry')}
                                </Button>
                            </Box>
                        ) : isCleartripFilterOnly && cleartripFallbackTransactions.length === 0 ? (
                            <WalletEmptyStateWrapper />
                        ) : isCleartripFilterOnly ? (
                            <CleartripTransactions
                                transactions={cleartripFallbackTransactions}
                                hasMoreTransactions={hasMoreTransactions}
                                isFetchingMore={isFetchingMore}
                                onLoadMore={onLoadMore}
                                formatAmount={formatAmount}
                                transactionHistoryLabel={cleartripTransactionLabels?.transactionHistoryLabel || t('transactionHistory')}
                                noTransactionsLabel={cleartripTransactionLabels?.noTransactionsLabel || t('noTransactions')}
                                loadMoreLabel={cleartripTransactionLabels?.loadMoreLabel || t('loadMore')}
                                loadingLabel={cleartripTransactionLabels?.loadingLabel || t('loading')}
                                transactionIdLabel={cleartripTransactionLabels?.transactionIdLabel || t('transactionId')}
                                isRtl={isRtl}
                                showHeader={false}
                            />
                        ) : formattedTransactions.length === 0 ? (
                            <WalletEmptyStateWrapper />
                        ) : (
                            <>
                                {formattedTransactions.map(txn => {
                                    const daysUntilExpiry =
                                        activeTab !== 'USED' ? getDaysUntilExpiry(txn.expiryDate) : null
                                    const displayDate = formatDisplayDate(txn.transactionDate, locale)
                                    const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 10

                                    return (
                                        <Card
                                            key={txn.id}
                                            elevation={0}
                                            sx={{
                                                mb: 1.5,
                                                bgcolor: 'background.paper',
                                                borderRadius: componentRadius,
                                                border: 1,
                                                borderColor: 'divider'
                                            }}
                                        >
                                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 1.75 } }}>
                                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                                    {(() => {
                                                        const iconType = txn.iconType || 'card'
                                                        const IconComponent = getIconComponent(iconType)
                                                        return (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: 48,
                                                                    height: 48,
                                                                    borderRadius: '50%',
                                                                    bgcolor: theme =>
                                                                        alpha(theme.palette.primary.main, 0.08),
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                <IconComponent
                                                                    sx={{ fontSize: 28, color: 'primary.main' }}
                                                                />
                                                            </Box>
                                                        )
                                                    })()}

                                                    <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                mb: 0.75
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    fontSize: '0.9375rem',
                                                                    lineHeight: 1.4,
                                                                    color: 'text.primary'
                                                                }}
                                                            >
                                                                {txn.source?.trim() || '-'}
                                                            </Typography>
                                                        </Box>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontSize: '0.8125rem',
                                                                display: 'block'
                                                            }}
                                                        >
                                                            {displayDate}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ textAlign: 'right', flexShrink: 0, pt: 0.5 }}>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color:
                                                                    activeTab === 'EARNED' || activeTab === 'REFUND'
                                                                        ? 'success.main'
                                                                        : 'error.main',
                                                                fontSize: '1rem',
                                                                mb: 1,
                                                                lineHeight: 1.2
                                                            }}
                                                        >
                                                            {activeTab === 'EARNED' || activeTab === 'REFUND'
                                                                ? '+'
                                                                : '-'}
                                                            {formatAmount(txn.amount, txn.currency)}
                                                        </Typography>

                                                        {activeTab !== 'USED' &&
                                                            txn.expiryDate &&
                                                            txn.expiryDate !== '-' && (
                                                                <Chip
                                                                    label={
                                                                        daysUntilExpiry !== null &&
                                                                        daysUntilExpiry <= 10
                                                                            ? daysUntilExpiry === 1
                                                                                ? t('expiresInDays', {
                                                                                      days: daysUntilExpiry
                                                                                  })
                                                                                : t('expiresInDaysPlural', {
                                                                                      days: daysUntilExpiry
                                                                                  })
                                                                            : t('validTill', {
                                                                                  date: formatDisplayDate(txn.expiryDate, locale)
                                                                              })
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        height: 20,
                                                                        fontSize: '0.6875rem',
                                                                        fontWeight: 500,
                                                                        bgcolor: isExpiringSoon
                                                                            ? theme =>
                                                                                  alpha(
                                                                                      theme.palette.warning.main,
                                                                                      0.15
                                                                                  )
                                                                            : 'grey.200',
                                                                        color: isExpiringSoon
                                                                            ? 'warning.main'
                                                                            : 'text.secondary',
                                                                        '& .MuiChip-label': { px: 1, py: 0 }
                                                                    }}
                                                                />
                                                            )}
                                                    </Box>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        mt: 1.5,
                                                        pt: 1.5,
                                                        borderTop: 1,
                                                        borderColor: 'divider',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    {txn.reference && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}
                                                        >
                                                            {txn.reference}
                                                        </Typography>
                                                    )}
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.disabled',
                                                            fontSize: '0.6875rem',
                                                            fontFamily: 'monospace',
                                                            ml: 'auto'
                                                        }}
                                                    >
                                                        {t('transactionId')}: {txn.id}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    )
                                })}

                                {hasMoreTransactions && (
                                    <Box sx={{ textAlign: 'center', pt: 2, pb: 4 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={onLoadMore}
                                            disabled={isFetchingMore}
                                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                                        >
                                            {isFetchingMore ? t('loading') : t('loadMore')}
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                        </Box>
                    </Card>
                </>
            )}
        </Box>
    )
}

export { WalletDesktopPage }
export default WalletDesktopPage
